import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Input, Select, Table, Tag } from "@stonelog/stonelog-design-system";
import { GingaIcon } from "@stonelog/stonelog-ginga-icons";
import { getAllHubs, hubsSelector } from "../../../../features/senninha/hubsSlice";
import { compareTableColumn } from "../../../../utils/common";
import {
  FILTER_OPTIONS,
  FILTER_PLACEHOLDERS,
  formatDate,
  formatEnumLabel,
  getStatusTagType,
} from "../constants";

import * as S from "../styles";

const COLUMNS = [
  {
    title: "ID",
    dataIndex: "id",
    key: "id",
  },
  {
    title: "Tracking code",
    dataIndex: "tracking_code",
    key: "tracking_code",
    sorter: { compare: (a, b) => compareTableColumn(a, b, "tracking_code") },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status) => (
      <Tag text={formatEnumLabel(status)} type={getStatusTagType(status)} />
    ),
  },
  {
    title: "Destinatário",
    dataIndex: "recipientName",
    key: "recipientName",
    sorter: { compare: (a, b) => compareTableColumn(a, b, "recipientName") },
  },
  {
    title: "Polo atual",
    dataIndex: "poloName",
    key: "poloName",
    sorter: { compare: (a, b) => compareTableColumn(a, b, "poloName") },
  },
  {
    title: "Responsável atual",
    dataIndex: "responsavel",
    key: "responsavel",
    sorter: { compare: (a, b) => compareTableColumn(a, b, "responsavel") },
  },
  {
    title: "Prazo de entrega",
    dataIndex: "prazoEntrega",
    key: "prazoEntrega",
  },
];

// Filtros + tabela. `search` é o retorno de useDeliverySearch (estado da busca, lista e a
// paginação no servidor já pronta); `onSelect` recebe o id da delivery clicada.
const DeliveryTable = ({ search, onSelect }) => {
  const {
    filtro,
    valor,
    valorError,
    onChangeFiltro,
    onChangeValor,
    onSearch,
    deliveries,
    loading,
    pagination,
  } = search;

  const dispatch = useDispatch();
  const { allHubs, allHubsStatus } = useSelector(hubsSelector);

  useEffect(() => {
    if (!allHubs?.length) dispatch(getAllHubs());
  }, [dispatch]);

  const poloOptions = useMemo(
    () => (allHubs || []).map((polo) => ({ value: polo.id, label: polo.name })),
    [allHubs]
  );

  const dataSource = useMemo(
    () =>
      deliveries.map((d) => ({
        key: d.id,
        id: d.id,
        tracking_code: d.tracking_code,
        status: d.status,
        recipientName: d.recipient?.name || "-",
        poloName: d.polo_atual?.hub_name || "-",
        responsavel: d.responsaveis?.atual || "-",
        prazoEntrega: formatDate(d.deadlines?.delivery),
      })),
    [deliveries]
  );

  return (
    <>
      {/* "Buscar por" + valor. A API aceita um filtro por vez, então o campo de valor muda:
          select de polos para "polo", input de texto para os demais. */}
      <S.FilterContainer>
        <S.FilterContent>
          <S.FilterTypeField>
            <Select
              placeholder="Buscar por"
              label="Buscar por"
              size="small"
              value={filtro}
              onChange={onChangeFiltro}
              options={FILTER_OPTIONS}
              className="ginga-lib-select-table admin-stone-entrega-select"
              suffixIcon={
                <GingaIcon
                  name="chevron-down"
                  color="rgba(118, 127, 141, 1)"
                  size={21}
                />
              }
              showSearch
            />
          </S.FilterTypeField>
          <S.FilterValueField>
            {filtro === "polo" ? (
              <Select
                size="small"
                placeholder="Selecione um polo"
                label="Polo"
                value={valor || null}
                onChange={(val) => {
                  onChangeValor(val);
                  if (val) onSearch(val);
                }}
                status={valorError ? "error" : "default"}
                supportText={valorError}
                options={poloOptions}
                disabled={allHubsStatus === "pending"}
                allowClear
                className="admin-stone-entrega-select"
                showSearch
                optionFilterProp="label"
              />
            ) : (
              <Input
                size="small"
                label="Valor"
                value={valor}
                onChange={(e) => onChangeValor(e.target.value)}
                onPressEnter={() => onSearch()}
                status={valorError ? "error" : "default"}
                supportText={valorError}
                placeholder={FILTER_PLACEHOLDERS[filtro]}
                className="admin-stone-entrega-input"
                allowClear
              />
            )}
          </S.FilterValueField>
        </S.FilterContent>
      </S.FilterContainer>

      <Table
        columns={COLUMNS}
        dataSource={dataSource}
        loading={loading}
        className="ginga-lib-table"
        pagination={pagination}
        onRow={(row) => ({
          onClick: () => onSelect(row.id),
        })}
      />
    </>
  );
};

export default DeliveryTable;
